import React, {Fragment, useMemo, useRef, useState} from 'react';
import {Combobox, Transition} from "@headlessui/react";
import {ArrowTurnDownRightIcon, CheckIcon, ChevronUpDownIcon} from "@heroicons/react/24/solid";
import {classNames} from "../../../utils/common";

function buildParentPathById(categories) {
    const parentPathById = new Map();
    const stack = [];

    categories.forEach((categoryOption) => {
        while (stack.length && stack[stack.length - 1].level >= categoryOption.level) {
            stack.pop();
        }
        parentPathById.set(categoryOption.id, stack.map((ancestor) => ancestor.name).join(' › '));
        stack.push(categoryOption);
    });

    return parentPathById;
}

const CategoriesPicker = ({label, categories, setCategories, allCategories, ...props}) => {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);
    const isFiltering = query.trim().length > 0;

    function selectInputText(input = inputRef.current) {
        if (!input) {
            return;
        }
        // Defer so selection survives focus/open handling and mouseup caret placement.
        requestAnimationFrame(() => input.select());
    }

    const parentPathById = useMemo(() => buildParentPathById(allCategories), [allCategories]);

    const filteredCategories = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return allCategories;
        }
        return allCategories.filter(categoryOption =>
            categoryOption.name.toLowerCase().includes(normalizedQuery)
        );
    }, [allCategories, query]);

    function handleChange(nextCategories) {
        setCategories(nextCategories);
        setQuery('');
    }

    function renderCategoryLabel(categoryOption, {selected, active}) {
        const parentPath = parentPathById.get(categoryOption.id);

        if (isFiltering) {
            return (
                <span className={classNames(
                    selected ? 'font-semibold' : 'font-normal',
                    'ml-3 block truncate'
                )}>
                    {parentPath && (
                        <span className={active ? 'text-indigo-200' : 'text-gray-400'}>
                            {parentPath} ›{' '}
                        </span>
                    )}
                    {categoryOption.name}
                </span>
            );
        }

        return (
            <span
                className={classNames(
                    selected ? 'font-semibold' : 'font-normal',
                    'ml-3 flex items-center truncate'
                )}
                style={{paddingLeft: `${Math.max(categoryOption.level - 1, 0) * 16}px`}}
            >
                {categoryOption.level > 1 && (
                    <ArrowTurnDownRightIcon
                        className={classNames(
                            'h-4 w-4 mr-1.5 flex-shrink-0',
                            active ? 'text-indigo-200' : 'text-gray-400'
                        )}
                        aria-hidden="true"
                    />
                )}
                {categoryOption.name}
            </span>
        );
    }

    return (
        <Combobox as="div" value={categories} by="id" onChange={handleChange} multiple {...props}>
            {label && <Combobox.Label className="ml-3 block text-sm font-medium text-gray-700"> {label}</Combobox.Label>}
            <div className="mt-1 relative w-full min-w-[250px] max-w-sm">
                <div className="relative flex flex-wrap items-center gap-x-1.5 gap-y-1 min-h-10 w-full pl-3 pr-10 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-left focus-within:outline-none focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500 sm:text-sm">
                    {categories.map((category) => (
                        <span key={category.id} className="text-gray-900 whitespace-nowrap">
                            [{category.name}]
                        </span>
                    ))}
                    <Combobox.Input
                        ref={inputRef}
                        className="flex-1 min-w-[4.5rem] border-0 p-0 m-0 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
                        displayValue={() => ''}
                        onChange={(event) => setQuery(event.target.value)}
                        onFocus={(event) => selectInputText(event.target)}
                        onClick={(event) => selectInputText(event.target)}
                    />
                    <Combobox.Button
                        className="absolute top-0 right-0 h-10 flex items-center pr-3 focus:outline-none"
                        onClick={() => selectInputText()}
                    >
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                    </Combobox.Button>
                </div>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredCategories.length === 0 && (
                            <div className="py-2 px-3 text-gray-500 cursor-default select-none">
                                No categories found
                            </div>
                        )}
                        {filteredCategories.map((categoryOption) => (
                            <Combobox.Option
                                key={categoryOption.id}
                                className={({ active }) =>
                                    classNames(active
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-gray-900',
                                        'relative cursor-default select-none py-2 pl-3 pr-9')
                                }
                                value={categoryOption}
                            >
                                {({ selected, active }) => (
                                    <>
                                        <div className="flex items-center">
                                            {renderCategoryLabel(categoryOption, {selected, active})}
                                        </div>
                                        {selected && <span className={classNames(
                                            active ? 'text-white' : 'text-indigo-600',
                                            'absolute inset-y-0 right-0 flex items-center pr-4'
                                        )}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                        </span>}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default CategoriesPicker;
